import { plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { intlLocale } from '../../lib/locale';
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent } from 'react';
import Chip from '../../components/Chip';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import type { GraphModel } from './graphModel';
import { classColor } from './graphModel';
import type { SimEdge, SimNode } from './simulation';
import { EntityIcon } from './ExplorerShared';
import { classLabel, relationLabel } from './explorerModel';
import './focus-graph.css';

interface FocusGraphProps {
  model: GraphModel;
  rootId: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

type Branches = Record<string, string[]>;
interface PlacedNode {
  node: SimNode;
  parentId: string | null;
  depth: number;
  x: number;
  y: number;
}

const CARD_WIDTH = 256;
const CARD_HEIGHT = 154;
const COLUMN = 446;
const ROW = 194;
const PADDING = 36;
const HEADER = 72;
const BATCH = 4;

/** A saved expansion is only valid while the underlying reference still exists. */
function branchChildren(model: GraphModel, id: string, branches: Branches): string[] {
  return [...new Set(branches[id] ?? [])].filter((child) => child !== id && model.byId.has(child) && model.neighbours.get(id)?.has(child));
}

function placedChildren(positions: Map<string, PlacedNode>, id: string, branches: Branches): string[] {
  return [...new Set(branches[id] ?? [])].filter((child) => positions.get(child)?.parentId === id);
}

/** A tidy tree assigns each leaf its own row. There is no simulation or overlap. */
function placeTree(model: GraphModel, rootId: string, branches: Branches) {
  const placed: PlacedNode[] = [];
  const seen = new Set<string>();
  const positions = new Map<string, PlacedNode>();
  const pending: { id: string; parentId: string | null; depth: number; visited: boolean }[] = [
    { id: rootId, parentId: null, depth: 0, visited: false },
  ];
  let leaves = 0;
  let maxDepth = 0;
  // Iterative postorder also supports very deep chains without a recursive stack.
  while (pending.length) {
    const entry = pending.pop()!;
    if (!entry.visited) {
      if (seen.has(entry.id) || !model.byId.has(entry.id)) continue;
      seen.add(entry.id);
      pending.push({ ...entry, visited: true });
      const children = branchChildren(model, entry.id, branches);
      for (let index = children.length - 1; index >= 0; index -= 1) {
        pending.push({ id: children[index]!, parentId: entry.id, depth: entry.depth + 1, visited: false });
      }
      continue;
    }
    const children = branchChildren(model, entry.id, branches).flatMap((id) => {
      const child = positions.get(id);
      return child && child.parentId === entry.id ? [child] : [];
    });
    const y = children.length
      ? (children[0]!.y + children[children.length - 1]!.y) / 2
      : HEADER + leaves++ * ROW;
    const position = {
      node: model.byId.get(entry.id)!, parentId: entry.parentId, depth: entry.depth,
      x: PADDING + entry.depth * COLUMN, y,
    };
    placed.push(position);
    positions.set(entry.id, position);
    maxDepth = Math.max(maxDepth, entry.depth);
  }
  // A root-first DOM order makes keyboard navigation follow the exploration.
  placed.sort((a, b) => a.depth - b.depth || a.y - b.y);
  return {
    placed, positions, maxDepth,
    width: PADDING * 2 + maxDepth * COLUMN + CARD_WIDTH,
    height: Math.max(460, HEADER + Math.max(0, leaves - 1) * ROW + CARD_HEIGHT + PADDING),
  };
}

function directNeighbours(model: GraphModel, id: string) {
  const locale = intlLocale();
  return [...(model.neighbours.get(id) ?? [])]
    .filter((other) => other !== id && model.byId.has(other))
    .sort((a, b) => {
      const left = model.byId.get(a)!;
      const right = model.byId.get(b)!;
      return left.klass.localeCompare(right.klass, locale) || left.label.localeCompare(right.label, locale) || a.localeCompare(b);
    });
}

function edgesBetween(model: GraphModel, parentId: string, childId: string): SimEdge[] {
  return (model.incident.get(parentId) ?? []).filter((edge) =>
    (edge.source.id === parentId && edge.target.id === childId)
    || (edge.target.id === parentId && edge.source.id === childId));
}

function edgeDirections(edges: SimEdge[], fromId: string) {
  return {
    forward: edges.some((edge) => edge.source.id === fromId),
    reverse: edges.some((edge) => edge.target.id === fromId),
  };
}

/** "Fit to view" has no counterpart in the shared icon set. */
function FitIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M7 3H3v4m10-4h4v4M3 13v4h4m10-4v4h-4M7 7h6v6H7z" />
    </svg>
  );
}

export function FocusGraph(props: FocusGraphProps) {
  // A changed starting point creates a fresh exploration, view switches do not.
  return <FocusWorkspace key={props.rootId} {...props} />;
}

function FocusWorkspace({ model, rootId, selectedId, onSelect }: FocusGraphProps) {
  const [branches, setBranches] = useState<Branches>({});
  const [zoom, setZoom] = useState(1);
  const { t } = useLingui();
  // null = the initial hint, rendered in the active language.
  const [notice, setNotice] = useState<string | null>(null);
  const [crossLinkNodeId, setCrossLinkNodeId] = useState<string | null>(null);
  const crossLinkPanel = useRef<HTMLElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const cardElements = useRef(new Map<string, HTMLElement>());
  const anchor = useRef<{ id: string; top: number; left: number } | null>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const arrowId = `focus-arrow-${useId().replace(/:/g, '')}`;
  const layout = useMemo(() => placeTree(model, rootId, branches), [model, rootId, branches]);
  const visible = useMemo(() => new Set(layout.placed.map((entry) => entry.node.id)), [layout]);
  const root = model.byId.get(rootId);
  const crossLinkNode = crossLinkNodeId ? layout.positions.get(crossLinkNodeId) : undefined;
  const crossLinkEntries = crossLinkNode ? directNeighbours(model, crossLinkNode.node.id)
    .filter((id) => visible.has(id) && id !== crossLinkNode.parentId && !placedChildren(layout.positions, crossLinkNode.node.id, branches).includes(id))
    .map((id) => ({ node: model.byId.get(id)!, edges: edgesBetween(model, crossLinkNode.node.id, id) })) : [];

  useLayoutEffect(() => {
    const saved = anchor.current;
    if (!saved || !viewport.current) return;
    const element = cardElements.current.get(saved.id);
    if (element) {
      const bounds = element.getBoundingClientRect();
      viewport.current.scrollLeft += bounds.left - saved.left;
      viewport.current.scrollTop += bounds.top - saved.top;
    }
    anchor.current = null;
  }, [layout, zoom]);

  useLayoutEffect(() => {
    if (crossLinkNodeId) crossLinkPanel.current?.focus({ preventScroll: false });
  }, [crossLinkNodeId]);

  function rememberPosition(id: string) {
    const bounds = cardElements.current.get(id)?.getBoundingClientRect();
    if (bounds) anchor.current = { id, top: bounds.top, left: bounds.left };
  }

  function expand(id: string) {
    const next = directNeighbours(model, id).filter((other) => !visible.has(other)).slice(0, BATCH);
    if (!next.length) return;
    if (visible.size > 1) rememberPosition(id);
    setBranches((current) => ({ ...current, [id]: [...(current[id] ?? []), ...next] }));
    const opened = next.length;
    const nodeLabel = model.byId.get(id)?.label;
    const nowVisible = visible.size + opened;
    setNotice(nodeLabel === undefined
      ? t`${plural(opened, { one: '# Verbindung', other: '# Verbindungen' })} dieses Knotens geöffnet, ${plural(nowVisible, { one: '# Knoten', other: '# Knoten' })} sichtbar.`
      : t`${plural(opened, { one: '# Verbindung', other: '# Verbindungen' })} von ${nodeLabel} geöffnet, ${plural(nowVisible, { one: '# Knoten', other: '# Knoten' })} sichtbar.`);
  }

  function collapse(id: string) {
    rememberPosition(id);
    const removed = new Set<string>();
    const pending = placedChildren(layout.positions, id, branches);
    while (pending.length) {
      const child = pending.pop()!;
      if (removed.has(child)) continue;
      removed.add(child);
      pending.push(...placedChildren(layout.positions, child, branches));
    }
    setBranches((current) => {
      const next = { ...current };
      delete next[id];
      for (const child of removed) delete next[child];
      return next;
    });
    if (selectedId && removed.has(selectedId)) onSelect(id);
    const removedCount = removed.size;
    setNotice(t`${plural(removedCount, { one: '# Knoten', other: '# Knoten' })} eingeklappt.`);
  }

  function setScale(value: number) {
    if (selectedId && visible.has(selectedId)) rememberPosition(selectedId);
    else rememberPosition(rootId);
    setZoom(Math.max(0.75, Math.min(1.25, value)));
  }

  function fit() {
    const pane = viewport.current;
    if (!pane) return;
    const scale = Math.min(1, (pane.clientWidth - 24) / layout.width, (pane.clientHeight - 24) / layout.height);
    anchor.current = null;
    setZoom(Math.max(0.75, scale));
    pane.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    setNotice(scale < 0.75
      ? t`Auf 75 % verkleinert. Der Rest ist per Scrollen erreichbar.`
      : t`Graph eingepasst.`);
  }

  function reset() {
    anchor.current = null;
    setBranches({});
    setZoom(1);
    setCrossLinkNodeId(null);
    onSelect(rootId);
    viewport.current?.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    setNotice(t`Ansicht zurückgesetzt.`);
  }

  function locate(id: string) {
    onSelect(id);
    const position = layout.positions.get(id);
    const pane = viewport.current;
    if (!position || !pane) return;
    pane.scrollTo({ left: Math.max(0, (position.x + CARD_WIDTH / 2) * zoom - pane.clientWidth / 2), top: Math.max(0, (position.y + CARD_HEIGHT / 2) * zoom - pane.clientHeight / 2), behavior: 'instant' });
    cardElements.current.get(id)?.querySelector<HTMLButtonElement>('.focus-node-main')?.focus({ preventScroll: true });
  }

  function closeCrossLinks() {
    if (crossLinkNodeId) cardElements.current.get(crossLinkNodeId)?.querySelector<HTMLButtonElement>('.focus-node-meta button')?.focus({ preventScroll: true });
    setCrossLinkNodeId(null);
  }

  function startPan(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0 || event.pointerType === 'touch' || (event.target as HTMLElement).closest('button, a, input, [data-focus-card]')) return;
    const pane = viewport.current;
    if (!pane) return;
    drag.current = { x: event.clientX, y: event.clientY, left: pane.scrollLeft, top: pane.scrollTop };
    pane.setPointerCapture(event.pointerId);
    pane.classList.add('is-panning');
  }

  function pan(event: PointerEvent<HTMLDivElement>) {
    const start = drag.current;
    const pane = viewport.current;
    if (!start || !pane) return;
    pane.scrollLeft = start.left - (event.clientX - start.x);
    pane.scrollTop = start.top - (event.clientY - start.y);
  }

  function stopPan() {
    drag.current = null;
    viewport.current?.classList.remove('is-panning');
  }

  if (!root) return <EmptyState title={t`Kein Ausgangspunkt gewählt`} />;

  const crossLinkLabel = crossLinkNode?.node.label ?? '';
  const visibleCount = visible.size;
  const levels = layout.maxDepth + 1;

  return (
    <section className="focus-graph" aria-label={t`Schrittweise erweiterbarer Beziehungsgraph`}>
      <div className="focus-graph-toolbar">
        <div className="focus-graph-counter"><Trans><strong>{visibleCount}</strong> Knoten sichtbar</Trans><span className="focus-graph-counter-separator">/</span><Plural value={levels} one="# Ebene" other="# Ebenen" /></div>
        <div className="focus-graph-tools">
          <div className="focus-graph-zoom" role="group" aria-label={t`Zoom`}>
            <button type="button" aria-label={t`Verkleinern`} title={t`Verkleinern`} disabled={zoom <= 0.75} onClick={() => setScale(zoom - 0.1)}><Icon name="minus" /></button>
            <output aria-label={t`Zoomstufe`}>{Math.round(zoom * 100)} %</output>
            <button type="button" aria-label={t`Vergrößern`} title={t`Vergrößern`} disabled={zoom >= 1.25} onClick={() => setScale(zoom + 0.1)}><Icon name="plus" /></button>
          </div>
          <button type="button" className="focus-graph-tool" onClick={fit} title={t`Einpassen, mindestens 75 % für lesbare Knoten`}><FitIcon /><span><Trans>Einpassen</Trans></span></button>
          <button type="button" className="focus-graph-tool" onClick={reset}><Icon name="refresh" /><span><Trans>Zum Anfang</Trans></span></button>
        </div>
      </div>
      {crossLinkNode && crossLinkEntries.length > 0 && <aside ref={crossLinkPanel} tabIndex={-1} className="focus-graph-crosslinks" aria-label={t`Querverbindungen von ${crossLinkLabel}`} onKeyDown={(event) => { if (event.key === 'Escape') closeCrossLinks(); }}>
        <div className="focus-graph-crosslinks-head"><div><strong><Trans>Querverbindungen</Trans></strong><span><Trans>Weitere Verbindungen von {crossLinkLabel} zu bereits sichtbaren Knoten.</Trans></span></div><button type="button" aria-label={t`Querverbindungen schließen`} onClick={closeCrossLinks}><Icon name="close" /></button></div>
        <div className="focus-graph-crosslinks-list">{crossLinkEntries.map(({ node, edges }) => <button type="button" key={node.id} className="focus-graph-crosslink" onClick={() => locate(node.id)} title={`${crossLinkNode.node.label} → ${edges.map((edge) => relationLabel(edge, crossLinkNode.node.id)).join(' · ')} → ${node.label}`}><EntityIcon klass={node.klass} /><span><small>{edges.map((edge) => relationLabel(edge, crossLinkNode.node.id)).join(' · ')}</small><strong>{node.label}</strong></span><Icon name="arrow-right" size={14} className="focus-graph-crosslink-arrow" /></button>)}</div>
      </aside>}
      <div
        ref={viewport} className="focus-graph-viewport" tabIndex={0}
        role="region" aria-label={t`Graph-Arbeitsfläche. Mit den Pfeiltasten scrollen oder die freie Fläche ziehen.`}
        onPointerDown={startPan} onPointerMove={pan} onPointerUp={stopPan} onPointerCancel={stopPan} onLostPointerCapture={stopPan}
      >
        <div className="focus-graph-scroll-area" style={{ width: (visible.size === 1 ? Math.max(layout.width, 870) : layout.width) * zoom, height: layout.height * zoom }}>
          <div className="focus-graph-board" style={{ width: visible.size === 1 ? Math.max(layout.width, 870) : layout.width, height: layout.height, transform: `scale(${zoom})` }}>
            {Array.from({ length: layout.maxDepth + 1 }, (_, depth) => (
              <div key={depth} className="focus-graph-lane" style={{ left: PADDING + depth * COLUMN, width: CARD_WIDTH }}>
                <span>{String(depth + 1).padStart(2, '0')}</span>{depth === 0 ? t`Start` : t`Schritt ${depth}`}
              </div>
            ))}
            <svg className="focus-graph-edges" width={layout.width} height={layout.height} aria-hidden="true">
              <defs><marker id={arrowId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M1 1L9 5L1 9" fill="none" stroke="currentColor" strokeWidth="1.5" /></marker></defs>
              {layout.placed.filter((entry) => entry.parentId).map((entry) => {
                const parent = layout.positions.get(entry.parentId!)!;
                const edges = edgesBetween(model, parent.node.id, entry.node.id);
                if (!edges.length) return null;
                const fromX = parent.x + CARD_WIDTH;
                const fromY = parent.y + CARD_HEIGHT / 2;
                const toX = entry.x - 5;
                const toY = entry.y + CARD_HEIGHT / 2;
                const elbowX = fromX + 30;
                const { forward, reverse } = edgeDirections(edges, parent.node.id);
                return <path key={entry.node.id} className={`focus-graph-edge${selectedId === entry.node.id || selectedId === parent.node.id ? ' is-selected' : ''}`} d={`M${fromX + (reverse ? 5 : 0)} ${fromY} C${elbowX} ${fromY}, ${elbowX} ${toY}, ${elbowX + 34} ${toY} L${toX} ${toY}`} markerEnd={forward ? `url(#${arrowId})` : undefined} markerStart={reverse ? `url(#${arrowId})` : undefined} />;
              })}
            </svg>
            {layout.placed.map((entry) => {
              const { node, parentId } = entry;
              const children = placedChildren(layout.positions, node.id, branches);
              const neighbours = directNeighbours(model, node.id);
              const remaining = neighbours.filter((id) => !visible.has(id)).length;
              const crossLinks = neighbours.filter((id) => visible.has(id) && id !== parentId && !children.includes(id)).length;
              const edges = parentId ? edgesBetween(model, parentId, node.id) : [];
              const nodeLabel = node.label;
              const toOpen = Math.min(BATCH, remaining);
              const edgeLabel = edges.map((edge) => `${edge.source.id === parentId ? '→' : '←'} ${relationLabel(edge, edge.source.id)}`).join(' · ');
              return (
                <div key={node.id}>
                  {edges.length > 0 && <div className="focus-graph-edge-label" title={edges.map((edge) => `${edge.source.label} → ${relationLabel(edge, edge.source.id)} → ${edge.target.label}`).join('\n')} style={{ left: entry.x - 145, top: entry.y + CARD_HEIGHT / 2 - 27 }}><span>{edgeLabel}</span></div>}
                  <article
                    data-focus-card="true" ref={(element) => { if (element) cardElements.current.set(node.id, element); else cardElements.current.delete(node.id); }}
                    className={`focus-node${selectedId === node.id ? ' is-selected' : ''}${node.id === rootId ? ' is-root' : ''}`}
                    style={{ left: entry.x, top: entry.y, width: CARD_WIDTH, height: CARD_HEIGHT, '--node-color': classColor(node.klass) } as CSSProperties}
                  >
                    <button type="button" className="focus-node-main" onClick={() => onSelect(node.id)} aria-pressed={selectedId === node.id} title={`${classLabel(node.klass)}: ${node.label}${node.detail ? ` · ${node.detail}` : ''}`}>
                      <EntityIcon klass={node.klass} className="focus-node-icon" />
                      <span className="focus-node-heading"><span className="focus-node-class">{classLabel(node.klass)}</span><strong>{node.label}</strong></span>
                      {(node.state === 'danger' || node.state === 'warn') && <span className={`focus-node-risk is-${node.state}`} title={node.state === 'danger' ? t`Risiko` : t`Prüfen`} aria-label={node.state === 'danger' ? t`Risiko` : t`Prüfen`} />}
                    </button>
                    <div className="focus-node-meta"><span><Plural value={neighbours.length} one="# Verbindung" other="# Verbindungen" /></span>{crossLinks > 0 && <button type="button" onClick={() => { onSelect(node.id); setCrossLinkNodeId((current) => current === node.id ? null : node.id); }} aria-expanded={crossLinkNodeId === node.id} title={t`Verbindungen zu bereits sichtbaren Knoten anzeigen`}><Trans>{crossLinks} Querverbind.</Trans></button>}</div>
                    <div className="focus-node-actions">
                      {remaining > 0 ? <button type="button" className="focus-node-expand" onClick={() => expand(node.id)} aria-label={children.length
                        ? t`${plural(toOpen, { one: '# weitere Verbindung', other: '# weitere Verbindungen' })} von ${nodeLabel} öffnen`
                        : t`${plural(toOpen, { one: '# Verbindung', other: '# Verbindungen' })} von ${nodeLabel} öffnen`}><Icon name="plus" size={14} />{children.length ? t`${toOpen} weitere` : t`Erweitern`}<Chip>{remaining}</Chip></button>
                        : <span className="focus-node-complete"><Icon name="check" size={14} />{neighbours.length ? t`Alle geöffnet` : t`Keine Verbindungen`}</span>}
                      {children.length > 0 && <button type="button" className="focus-node-collapse" onClick={() => collapse(node.id)} aria-label={t`Pfad von ${nodeLabel} einklappen`} title={t`Diesen Pfad einklappen`}><Icon name="minus" /></button>}
                    </div>
                  </article>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="focus-graph-footer"><span className="focus-graph-status" role="status" aria-live="polite">{notice ?? t`Mit „Erweitern“ die Verbindungen eines Knotens öffnen.`}</span><span className="focus-graph-pan-hint"><Trans>Ziehen oder scrollen zum Bewegen</Trans></span></div>
    </section>
  );
}
